// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { isoDateSchema, paginationQuerySchema, uuidSchema } from '../common.js';
/**
 * The line manager's team workspace (HR portal Prompt 5, Finance B-61 … B-66): today's picture of every direct report
 * (primary or secondary), their daily records and leave, and the two counts behind the manager badge. Every endpoint is
 * scoped to the caller's direct reports (`app.team_employee_ids()`) AND the team key the RLS predicate requires
 * (attendance.view_team / leave.view_team) or the organisation-wide key (attendance.view / leave.view, branch scope applies).
 */
/** How a report's day reads on the team board (derived from the engine's daily record, leave and today's punches). */
export const TEAM_DAY_STATUSES = ['present', 'late', 'absent', 'on_leave', 'missing_punch', 'weekly_off', 'holiday', 'not_in_yet', 'not_scheduled'];
/** In / out from today's normalised punches (IN = the last punch opened a work segment). */
export const LIVE_PUNCH_STATES = ['IN', 'OUT', 'NONE'];
/** Primary (`manager_employee_id`) or secondary (`secondary_manager_employee_id`) line manager of the report. */
export const TEAM_RELATIONS = ['primary', 'secondary'];
const MAX_RANGE_DAYS = 62;
const rangeOk = (v) => Date.parse(`${v.to}T00:00:00Z`) - Date.parse(`${v.from}T00:00:00Z`) <= (MAX_RANGE_DAYS - 1) * 86_400_000;
// ----- today ------------------------------------------------------------------------------------------------------------------
/** GET /orgs/:orgId/team/summary?date — absent date = the organisation's today. */
export const teamSummaryQuerySchema = z.object({ date: isoDateSchema.optional() });
// ----- attendance ---------------------------------------------------------------------------------------------------------------
/**
 * GET /orgs/:orgId/team/attendance — the daily records of the caller's direct reports over [from, to] (at most 62 days),
 * paginated by REPORT so a report's range is never split across pages (a month grid needs every day of a row).
 */
export const teamAttendanceQuerySchema = paginationQuerySchema.extend({
    from: isoDateSchema,
    to: isoDateSchema,
    employeeId: uuidSchema.optional(),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
}).refine((v) => v.to >= v.from, { message: 'to must be on/after from', path: ['to'] })
    .refine(rangeOk, { message: `At most ${MAX_RANGE_DAYS} days`, path: ['to'] });
// ----- leave ------------------------------------------------------------------------------------------------------------------
/** GET /orgs/:orgId/team/leave — approved, pending and info-requested leave of the direct reports overlapping [from, to]. */
export const teamLeaveQuerySchema = z.object({ from: isoDateSchema, to: isoDateSchema })
    .refine((v) => v.to >= v.from, { message: 'to must be on/after from', path: ['to'] })
    .refine(rangeOk, { message: `At most ${MAX_RANGE_DAYS} days`, path: ['to'] });
//# sourceMappingURL=team.js.map